/**
 * WDS (Workflow Dispute / Damage Status) Store — replaces ad-hoc `$rootScope.G_WDS`,
 * `wdsMessage`, and `removedClaimStatusList` reads in the audit/damages flow.
 *
 * G_WDS is a dynamic record produced by the AngularJS audit results workflow;
 * its shape varies (item-type, results data, claim modifications, etc.). We
 * type it as a generic object map and let consumers narrow.
 */

import { create } from 'zustand';
import { setRootScopeProperty, getRootScopeProperty } from '@/lib/legacyContext';
import { getMountConfig } from '@/mountConfig';

export type WdsRecord = Record<string, unknown> | null;

interface WdsState {
  /** $rootScope.G_WDS — opaque audit/dispute workflow record */
  wds: WdsRecord;
  /** wdsMessage — UI banner string used by the disputes flow */
  wdsMessage: string | null;
  /** removedClaimStatusList — claim statuses removed in the current edit session */
  removedClaimStatusList: unknown[];
}

interface WdsActions {
  setWds: (v: WdsRecord) => void;
  setWdsMessage: (v: string | null) => void;
  setRemovedClaimStatusList: (v: unknown[]) => void;
  hydrateFromLegacy: () => void;
  reset: () => void;
}

type WdsStore = WdsState & WdsActions;

const INITIAL_STATE: WdsState = {
  wds: null,
  wdsMessage: null,
  removedClaimStatusList: [],
};

function syncToLegacy(key: string, value: unknown): void {
  try {
    if (getMountConfig().mode === 'embedded') {
      setRootScopeProperty(key, value);
    }
  } catch {
    // graceful
  }
}

export const useWdsStore = create<WdsStore>()((set) => ({
  ...INITIAL_STATE,

  setWds: (v) => {
    set({ wds: v });
    syncToLegacy('G_WDS', v);
  },
  setWdsMessage: (v) => {
    set({ wdsMessage: v });
    syncToLegacy('wdsMessage', v);
    if (typeof window !== 'undefined') {
      if (v) window.sessionStorage.setItem('wdsMessage', v);
      else window.sessionStorage.removeItem('wdsMessage');
    }
  },
  setRemovedClaimStatusList: (v) => {
    set({ removedClaimStatusList: v });
    syncToLegacy('removedClaimStatusList', v);
  },

  hydrateFromLegacy: () => {
    // Embedded-only — see propertyStore.hydrateFromLegacy for full reasoning.
    if (getMountConfig().mode !== 'embedded') return;
    set({
      wds: getRootScopeProperty<WdsRecord>('G_WDS') ?? null,
      wdsMessage: getRootScopeProperty<string>('wdsMessage') ?? null,
      removedClaimStatusList: getRootScopeProperty<unknown[]>('removedClaimStatusList') ?? [],
    });
  },

  reset: () => set({ ...INITIAL_STATE }),
}));

export const useWds = (): WdsRecord => useWdsStore((s) => s.wds);
export const useWdsMessage = (): string | null => useWdsStore((s) => s.wdsMessage);
export const useRemovedClaimStatusList = (): unknown[] =>
  useWdsStore((s) => s.removedClaimStatusList);

/** Test-only. */
export function __resetWdsStoreForTests(): void {
  useWdsStore.setState({ ...INITIAL_STATE });
}
